import React from 'react';
import {
  Lock,
  Crown,
  Key,
  Code,
  Webhook,
  Database,
  Shield,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';

/**
 * Private Integrations Section
 * 
 * API access and private integrations - entitlement-gated.
 */
const PrivateIntegrationsSection = ({ agency, agencyId, entitlements, isReadOnly }) => {
  // Check if API access is entitled
  const isApiEntitled = entitlements?.api_access && entitlements.api_access !== 'hidden';
  const isLocked = entitlements?.api_access === 'locked';
  
  if (!isApiEntitled) {
    return (
      <div className="space-y-6" data-testid="private-integrations-locked">
        <div className="border-b border-slate-200 pb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
              <Lock className="text-slate-600" size={20} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Private Integrations</h2>
              <p className="text-sm text-slate-500">
                API access and custom integrations
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-8 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-4">
            <Lock className="text-slate-400" size={32} />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">
            API Access Not Available
          </h3>
          <p className="text-slate-500 mb-4 max-w-md mx-auto">
            Private integrations and API access require an enterprise plan. 
            Contact your account manager to unlock these capabilities.
          </p>
          <Badge className="bg-amber-100 text-amber-700 border-0">
            <Crown size={12} className="mr-1" />
            Enterprise Only
          </Badge>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="private-integrations-section">
      <div className="border-b border-slate-200 pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
              <Lock className="text-indigo-600" size={20} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Private Integrations</h2>
              <p className="text-sm text-slate-500">
                API access and custom integrations
              </p>
            </div>
          </div>
          
          <Badge className="bg-emerald-100 text-emerald-700 border-0">
            <Shield size={12} className="mr-1" />
            API Access Enabled
          </Badge>
        </div>
      </div>
      
      {/* API Keys */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
            <Key size={14} className="inline mr-1" />
            API Keys
          </h3>
          {!isReadOnly && (
            <Button variant="outline" size="sm">Generate New Key</Button>
          )}
        </div>
        
        <div className="p-4 bg-slate-50 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-900">Production Key</p>
              <p className="text-xs text-slate-500 font-mono">fe_live_••••••••••••••••</p>
            </div>
            <Badge variant="outline">Active</Badge>
          </div>
        </div>
        
        <div className="p-4 bg-slate-50 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-900">Test Key</p>
              <p className="text-xs text-slate-500 font-mono">fe_test_••••••••••••••••</p>
            </div>
            <Badge variant="outline">Active</Badge>
          </div>
        </div>
      </div>
      
      {/* Webhooks */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
            <Webhook size={14} className="inline mr-1" />
            Webhooks
          </h3>
          {!isReadOnly && (
            <Button variant="outline" size="sm">Add Endpoint</Button>
          )}
        </div>
        
        <p className="text-sm text-slate-500 text-center py-4">
          No webhook endpoints configured
        </p>
      </div>
      
      {/* API Documentation */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
            <Code size={24} className="text-slate-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-slate-900">API Documentation</h3>
            <p className="text-sm text-slate-500">Access the full API reference and guides</p>
          </div>
          <Button variant="outline">
            View Docs
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PrivateIntegrationsSection;
